import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Layout,
  Typography,
  Button,
  Space,
  message,
  Dropdown,
  Spin,
  Progress,
  Modal,
  DatePicker,
  Input,
  Select,
} from "antd";
import {
  ReloadOutlined,
  DownloadOutlined,
  LoadingOutlined,
  SyncOutlined,
  ClearOutlined,
  SearchOutlined,
  UpOutlined,
  DownOutlined,
} from "@ant-design/icons";
import { format } from "date-fns";
import { AuditLogTable } from "./components/AuditLogTable";
import { AuditLogDetail } from "./components/AuditLogDetail";
import { AuditLogStats } from "./components/AuditLogStats";
import {
  AdvancedFilterBuilder,
  createEmptyFilterTree,
  serializeFilterTree,
  countLeaves,
  treeDepth,
} from "./components/AdvancedFilterBuilder";
import { fetchTraceLogsAdvanced } from "./services/advancedTraceLogsApi";
import { BookingAnalyticsApi } from "./services/bookingAnalyticsApi";
import { fetchTraceLogDetail } from "./services/analyticsApi";
import { transformTraceLog } from "./services/api";

const { RangePicker } = DatePicker;
const { Header, Content } = Layout;
const { Title, Text } = Typography;

const MAX_DEPTH = 5;
const MAX_CONDITIONS = 25;

const exportToCSV = (logs, filename) => {
  const headers = [
    "Session ID",
    "Module",
    "Log Name",
    "Status",
    "Severity",
    "Last Step",
    "Failed Step",
    "IP Address",
    "Started At",
    "Updated At",
  ];

  const rows = logs.map((log) => [
    log.session_id,
    log.module || "",
    log.log_name || "",
    log.status,
    log.severity || "",
    log.steps?.[log.steps.length - 1]?.step || "",
    log.steps?.find((s) => s.step_status === "failed")?.step || "",
    log.ip_address || "",
    log.created_at,
    log.updated_at,
  ]);

  const csvContent = [
    headers.join(","),
    ...rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}_${format(new Date(), "yyyyMMdd_HHmmss")}.csv`;
  link.click();
  URL.revokeObjectURL(link.href);
};

const exportToJSON = (logs, filename) => {
  const jsonContent = JSON.stringify(logs, null, 2);
  const blob = new Blob([jsonContent], { type: "application/json" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}_${format(new Date(), "yyyyMMdd_HHmmss")}.json`;
  link.click();
  URL.revokeObjectURL(link.href);
};

/** Count leaf conditions in the already-serialized (API-shaped) tree. */
function countSerializedLeaves(node) {
  if (!node) return 0;
  if (node.conditions) return node.conditions.reduce((sum, c) => sum + countSerializedLeaves(c), 0);
  return 1;
}

/**
 * Analytics Logs — advanced structured search over trace logs, backed by
 * GET /api/admin/v1/booking-analytics/trace-logs with a `filters` AND/OR
 * condition tree (requirements/API055, ioo-338). Same list/stats/detail UI
 * as Trace Logs, but replaces the flat status/module/search filter bar with
 * the AdvancedFilterBuilder — the date range stays a separate, flat param
 * that combines with the tree (IMPR002/PLAN095).
 */
const AnalyticsLogs = () => {
  const [filterTree, setFilterTree] = useState(createEmptyFilterTree());
  // Only set when the Advanced Filter Builder's own "Apply Filters" button is
  // clicked — kept separate from the live-edited `filterTree` draft so typing
  // in the quick bar below doesn't accidentally apply an in-progress, maybe
  // invalid, advanced edit.
  const [appliedAdvancedFilters, setAppliedAdvancedFilters] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [moduleFilter, setModuleFilter] = useState("all");
  const [moduleOptions, setModuleOptions] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Debounce the search box (700ms) so it auto-applies without a Search
  // button, matching the Trace Logs tab's existing pattern.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 700);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const [logs, setLogs] = useState(null);
  const [logsTableData, setLogsTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const [exportProgress, setExportProgress] = useState(0);
  const [showProgress, setShowProgress] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Quick bar (search/status/module — instant/debounced) and the Advanced
  // Filter Builder (explicit Apply) both feed the same `filters` tree —
  // merged here as top-level AND leaves, since the API takes exactly one
  // `filters` param (requirements/API055). Status/Module apply immediately
  // on change and Search auto-applies 700ms after typing stops; only the
  // Advanced Builder's own conditions wait for its "Apply Filters" button.
  const appliedFilters = useMemo(() => {
    const leaves = [];
    if (statusFilter !== "all") leaves.push({ field: "status", operator: "is", value: statusFilter });
    if (moduleFilter !== "all") leaves.push({ field: "module", operator: "is", value: moduleFilter });
    if (debouncedSearch.trim()) leaves.push({ field: "keyword", operator: "contains", value: debouncedSearch.trim() });

    const combined = [...leaves, ...(appliedAdvancedFilters ? [appliedAdvancedFilters] : [])];

    if (combined.length === 0) return null;
    if (combined.length === 1) return combined[0];
    return { operator: "AND", conditions: combined };
  }, [statusFilter, moduleFilter, debouncedSearch, appliedAdvancedFilters]);

  const loadLogs = useCallback(
    async (page = 1, silent = false) => {
      if (!silent) setLoading(true);
      try {
        const response = await fetchTraceLogsAdvanced(page, {
          dateRange,
          filters: appliedFilters,
        });
        setLogs(response.statsData);
        setLogsTableData(response.logs);
        setPagination(response.pagination);
        setLastRefreshed(new Date());
        if (response.modules?.length) {
          setModuleOptions(response.modules);
        }
      } catch (error) {
        console.error("Failed to fetch analytics logs:", error);
        if (!silent) message.error("Failed to load analytics logs");
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [dateRange, appliedFilters],
  );

  // Reload whenever the applied filter tree or date range changes
  useEffect(() => {
    setCurrentPage(1);
    loadLogs(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, appliedFilters]);

  const handlePageChange = useCallback(
    (page) => {
      setCurrentPage(page);
      loadLogs(page);
    },
    [loadLogs],
  );

  const handleRowClick = async (log) => {
    setSelectedLog(log);
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const identifier = log.id || log.correlationId;
      const detail = await fetchTraceLogDetail(identifier);
      setSelectedLog(transformTraceLog(detail));
    } catch (error) {
      console.error("Failed to fetch trace log detail:", error);
      message.error("Failed to load trace log details");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleApplyFilters = () => {
    const leafCount = countLeaves(filterTree);
    const depth = treeDepth(filterTree);
    if (leafCount > MAX_CONDITIONS) {
      message.error(`Too many conditions (${leafCount}) — the API allows a maximum of ${MAX_CONDITIONS}.`);
      return;
    }
    if (depth > MAX_DEPTH) {
      message.error(`Filter tree too deeply nested (${depth}) — the API allows a maximum depth of ${MAX_DEPTH}.`);
      return;
    }
    setAppliedAdvancedFilters(serializeFilterTree(filterTree));
  };

  const handleResetFilters = () => {
    setFilterTree(createEmptyFilterTree());
    setAppliedAdvancedFilters(null);
    setDateRange(null);
    setSearchQuery("");
    setDebouncedSearch("");
    setStatusFilter("all");
    setModuleFilter("all");
  };

  const handleRefresh = () => {
    loadLogs(currentPage);
    message.info("Refreshing logs...");
  };

  const handleExport = async (type) => {
    if (logsTableData.length === 0) {
      message.warning("No logs to export");
      return;
    }

    setIsExporting(true);
    setShowProgress(true);
    setExportProgress(0);

    try {
      let allRawData = [];
      const perPage = 100;

      const fetchPage = (pageNum) =>
        BookingAnalyticsApi.getLogsAdvanced({
          page: pageNum,
          per_page: perPage,
          date_from: dateRange?.[0]?.format("YYYY-MM-DD"),
          date_to: dateRange?.[1]?.format("YYYY-MM-DD"),
          filters: appliedFilters,
        });

      const firstRes = await fetchPage(1);
      if (!firstRes?.success) throw new Error(firstRes?.message || "Failed to fetch logs");

      const paginatedData = firstRes?.data;
      const totalPages = paginatedData?.meta?.last_page || 1;
      allRawData.push(...(paginatedData?.data || []));
      setExportProgress(Math.round((1 / totalPages) * 100));

      for (let pageNum = 2; pageNum <= totalPages; pageNum++) {
        const res = await fetchPage(pageNum);
        if (!res?.success) throw new Error(`Failed to fetch page ${pageNum}`);
        allRawData.push(...(res?.data?.data || []));
        setExportProgress(Math.round((pageNum / totalPages) * 100));
      }

      if (type === "csv") {
        exportToCSV(allRawData, "analytics_logs");
      } else {
        exportToJSON(allRawData, "analytics_logs");
      }

      setExportProgress(100);
      message.success(`Successfully exported ${allRawData.length} logs`);
    } catch (error) {
      console.error("Export error:", error);
      message.error("Failed to export logs");
    } finally {
      setIsExporting(false);
      setTimeout(() => {
        setShowProgress(false);
        setExportProgress(0);
      }, 1000);
    }
  };

  const exportMenuItems = [
    { key: "csv", label: "Export as CSV", onClick: () => handleExport("csv") },
    { key: "json", label: "Export as JSON", onClick: () => handleExport("json") },
  ];

  const activeFilterCount = countSerializedLeaves(appliedFilters);

  return (
    <Layout style={{ background: "transparent" }}>
      {/* Header */}
      <Header
        style={{
          background: "#fff",
          borderBottom: "1px solid #f0f0f0",
          padding: "0 24px",
          height: "auto",
          lineHeight: "normal",
          position: "sticky",
          top: 48,
          zIndex: 8,
          boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
          marginLeft: -24,
          marginRight: -24,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "14px 0",
            gap: 16,
          }}
        >
          <Space size="middle" align="center">
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: "linear-gradient(135deg, #008385 0%, #00b3b5 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 8px rgba(0,131,133,0.3)",
                flexShrink: 0,
              }}
            >
              <SearchOutlined style={{ color: "#fff", fontSize: 18 }} />
            </div>
            <div>
              <Title level={4} style={{ margin: 0, justifySelf: "left" }}>
                Analytics Logs
              </Title>
              <Text type="secondary" style={{ fontSize: 12, lineHeight: "1.4" }}>
                Advanced AND/OR structured search across trace logs
              </Text>
            </div>
          </Space>
          <Space size={8}>
            <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight" disabled={isExporting}>
              <Button icon={isExporting ? <LoadingOutlined /> : <DownloadOutlined />} loading={isExporting} disabled={isExporting} style={{ borderRadius: 8, fontWeight: 500 }}>
                {isExporting ? "Exporting..." : "Export"}
              </Button>
            </Dropdown>
            <Button icon={loading ? <LoadingOutlined /> : <ReloadOutlined />} onClick={handleRefresh} disabled={loading}>
              Refresh
            </Button>
          </Space>
        </div>
      </Header>

      {/* Main Content */}
      <Content style={{ padding: "20px 24px" }}>
        <Spin spinning={loading} className="custom-spinner" tip="Loading analytics logs...">
          <Space direction="vertical" size={20} style={{ width: "100%" }}>
            {/* Export Progress Modal */}
            <Modal
              open={showProgress}
              title={
                <Space>
                  <SyncOutlined spin style={{ color: "#7c3aed" }} />
                  <span>Exporting Logs</span>
                </Space>
              }
              footer={null}
              closable={false}
              centered
            >
              <div style={{ padding: "8px 0 16px" }}>
                <Progress
                  percent={exportProgress}
                  status={exportProgress === 100 ? "success" : "active"}
                  strokeWidth={10}
                  strokeColor={{ "0%": "#7c3aed", "100%": "#a78bfa" }}
                />
                <Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: "block", textAlign: "center" }}>
                  {exportProgress < 100 ? `Fetching data... ${exportProgress}%` : "Export complete!"}
                </Text>
              </div>
            </Modal>

            {/* Stats */}
            <AuditLogStats logs={logs} totalCount={pagination?.total} />

            {/* Quick Filter Bar */}
            <div
              style={{
                background: "#fff",
                border: "1px solid #f0f0f0",
                borderRadius: 10,
                padding: "14px 18px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                display: "flex",
                flexWrap: "wrap",
                gap: 10,
                alignItems: "center",
              }}
            >
              <Input
                placeholder="Search by session ID, module, log name..."
                prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onPressEnter={(e) => setDebouncedSearch(e.target.value)}
                allowClear
                style={{ flex: "2 1 240px", minWidth: 220 }}
              />
              <Select
                value={statusFilter}
                onChange={setStatusFilter}
                style={{ width: 160 }}
                options={[
                  { value: "all", label: "All Statuses" },
                  { value: "success", label: "Confirmed" },
                  { value: "pending", label: "In Progress" },
                  { value: "failed", label: "Failed" },
                  { value: "partial", label: "Partial" },
                  { value: "abandoned", label: "Abandoned" },
                  { value: "expired", label: "Expired" },
                ]}
              />
              <Select
                value={moduleFilter}
                onChange={setModuleFilter}
                style={{ width: 180 }}
                options={[
                  { value: "all", label: "All Modules" },
                  ...moduleOptions.map((m) => ({ value: m, label: m })),
                ]}
              />
              <RangePicker value={dateRange} onChange={setDateRange} allowClear format="DD/MM/YYYY" />
              <Button
                icon={showAdvanced ? <UpOutlined /> : <DownOutlined />}
                onClick={() => setShowAdvanced((v) => !v)}
                style={
                  showAdvanced
                    ? { borderColor: "#008385", color: "#008385" }
                    : undefined
                }
              >
                Advanced Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
              </Button>
              <Button icon={<ClearOutlined />} onClick={handleResetFilters}>
                Reset
              </Button>
            </div>

            {/* Advanced Filter Builder */}
            {showAdvanced && (
              <div
                className="aflog-fade-in"
                style={{
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: 8,
                  padding: 25,
                }}
              >
                <Text strong style={{ fontSize: 15, color: "#334155", display: "block", marginBottom: 20 }}>
                  Advanced Filter Builder
                </Text>
                <AdvancedFilterBuilder value={filterTree} onChange={setFilterTree} />
                <Space style={{ marginTop: 20 }}>
                  <Button type="primary" onClick={handleApplyFilters}>
                    Apply Filters
                  </Button>
                  <Button onClick={() => setShowAdvanced(false)}>Cancel</Button>
                </Space>
              </div>
            )}

            {/* Results Count */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Text type="secondary" style={{ fontSize: 13 }}>
                Showing <strong style={{ color: "#111827" }}>{logsTableData.length}</strong> of{" "}
                <strong style={{ color: "#111827" }}>{pagination?.total || 0}</strong> attempts
              </Text>
              {lastRefreshed && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Last updated: {format(lastRefreshed, "HH:mm:ss")}
                </Text>
              )}
            </div>

            {/* Table */}
            <AuditLogTable
              logs={logsTableData}
              onRowClick={handleRowClick}
              pagination={pagination}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              loading={loading}
              hideLastStep
            />
          </Space>
        </Spin>
      </Content>

      {/* Detail Drawer */}
      <AuditLogDetail
        log={selectedLog}
        open={detailOpen}
        loading={detailLoading}
        onClose={() => {
          setDetailOpen(false);
          setSelectedLog(null);
        }}
      />
    </Layout>
  );
};

export default AnalyticsLogs;
